import bus from '@/lib/event_bus';
import { handleAuthFailure, handleAuthFailureError } from '@/lib/auth_failure';
import { get, hasOwnProperty, isEmpty } from '@/lib/helpers';
import { isCustomerAuthenticatedSync } from '@/lib/login_utils';
import { authState } from '@/store/getters/auth';
import { getLocalKey } from '@/store/local_store';
import { ACCESS_DETAILS } from '@/store/store_keys';

export const responseHelper =
  (onSuccess: any, onFailure: any) => (response: any) => {
    const { data } = response;
    if (data && data.success) {
      onSuccess(data);
    } else if (response.ok && response.status === 200) {
      onSuccess(data);
    } else {
      const { errorMessage, errorCode } = data;
      onFailure(errorCode, errorMessage, data);
    }
    return data;
  };

export const defaultCatch = (err: any, notification?: any) => {
  if (err.status === 477) {
    const errorResponse = get(err, 'data.error', {});
    if (!isEmpty(errorResponse)) {
      const {
        message = `Something went wrong ${
          notification && notification.action
            ? `while ${notification.action}`
            : ''
        }`,
      } = errorResponse;
      bus.$emit('notify', {
        type: 'error',
        title: (notification && notification.title) || 'Error',
        message,
      });
    }
  }
  // 401 and 403 both end the session - handleAuthFailure owns the notify and the
  // logout so every path through the app says the same thing.
  handleAuthFailure(err.status, 'defaultCatch');
  // The backstop for the same failure arriving with no status, because the
  // browser blocked a 401 that came back without a cors header. No url to pass
  // on - a rejected fetch carries none - so it falls back to the api root.
  handleAuthFailureError(err);
  bus.$emit('loader');
  console.log('Error occured while processing request', err);
};

interface FetchHandlerOptions {
  /**
   * Set false for the login and magic link endpoints. Those answer 401 for a
   * wrong otp or an expired link rather than for a dead session, and their own
   * callers already show the right message.
   */
  handleAuthFailure?: boolean;
}

/**
 * Normalises a raw fetch Response into the shape the so-fetch clients return.
 *
 * Auth failures are handled here as well, so the hand-rolled fetch calls get the
 * same logout as the calls that go through a so-fetch client. It is on by
 * default: a new endpoint added later is covered without anyone remembering to
 * opt in.
 */
export const fetchHandler = (
  response: any,
  {
    handleAuthFailure: shouldHandleAuthFailure = true,
  }: FetchHandlerOptions = {}
) => {
  if (shouldHandleAuthFailure) {
    handleAuthFailure(response.status, get(response, 'url', ''));
  }
  const defaultResp = {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
  };
  if (response.ok) {
    return response
      .json()
      .then((data: any) => {
        // the status was ok and there is a json body
        return Promise.resolve({ data, rawResp: response, ...defaultResp });
      })
      .catch((err: any) => {
        // the status was ok but there is no json body
        return Promise.resolve({
          data: err,
          rawResp: response,
          ...defaultResp,
        });
      });
  } else {
    return response
      .json()
      .catch((err: any) => {
        // the status was not ok and there is no json body
        return Promise.resolve({
          rawResp: response,
          data: err,
          ...defaultResp,
        });
      })
      .then((json: any) => {
        // the status was not ok but there is a json body
        return Promise.resolve({
          rawResp: response,
          data: json,
          ...defaultResp,
        });
      });
  }
};

/**
 * fetch, plus the guard for an auth failure the browser will not let us read.
 *
 * For the hand rolled calls that send the customer's bearer token: fetchHandler
 * covers them once a response arrives, but a 401 with no cors header never
 * becomes a response at all - it rejects. This looks at the rejection and then
 * rethrows it untouched, so the caller's own error handling is unchanged.
 */
export const authAwareFetch = (url: string, options?: any): Promise<any> =>
  fetch(url, options).catch((err: any) => {
    handleAuthFailureError(err, url);
    throw err;
  });

export const getHeaders = (defaultHeaders: object) => {
  const isAuthenticated = isCustomerAuthenticatedSync(authState());
  let headers: object = defaultHeaders;
  if (isAuthenticated) {
    const { access_token: accessToken } = authState();
    headers = {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
      'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh',
    };
    return headers;
  }
  const accessDetails: any = getLocalKey(ACCESS_DETAILS);
  if (
    accessDetails &&
    hasOwnProperty(accessDetails, 'access_token') &&
    accessDetails.access_token
  ) {
    headers = {
      ...headers,
      // Reads snake_case access_token, the actual field persisted in ACCESS_DETAILS —
      // camelCase accessToken doesn't exist on the stored object and previously sent
      // "Bearer undefined". Same fix applied in the React app the same day.
      Authorization: `Bearer ${accessDetails.access_token}`,
      'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh',
    };
    return headers;
  }
  return headers;
};

export function getErrorMessageFromResponse(
  soFetchResponse: any,
  defaultErrorMessage: string
) {
  if (hasOwnProperty(soFetchResponse, 'data')) {
    const { data } = soFetchResponse;
    return get(data, 'error.message', defaultErrorMessage);
  }
}
